import type { ShotKind } from '@/game/rules/schema';

/**
 * Client-only visual traits of units and buildings that fire, beyond their
 * models: where on the model a shot leaves. The sim decides when and what is
 * fired (`SimState.shots`, each with its rules `shot.kind`); the kind here
 * mirrors the rules so a view without a sim shot (the gallery) can still
 * draw one, and `visuals.test.ts` keeps the two in step.
 *
 * Shot kinds: tracer (a fast straight streak: rifles, machine guns), arrow (a
 * shallow arc), shell (a high arc, bursting on impact), missile (a smoke
 * trail, bursting on impact), beam (a straight energy beam for a moment).
 */
export type { ShotKind };

/**
 * Where a shot leaves a model, in world units from its tile centre on the
 * tile surface (a unit's `UNIT_SCALE` already applied). Units turn to face
 * their path, not their target, so theirs is only a height (x = z = 0);
 * buildings never turn, so theirs may sit off centre (a silo's shaft).
 */
export interface Muzzle {
  x: number;
  y: number;
  z: number;
}

export interface ShooterVisual {
  shot: ShotKind;
  muzzle: Muzzle;
}

const at = (y: number, x = 0, z = 0): Muzzle => ({ x, y, z });

/**
 * Unit or building type → how its shots look. A list is per level (entry i is
 * level i + 1, levels past the end use the last), for a city whose weapons
 * and skyline change as it grows. Types not listed fire no shots (melee).
 */
export const UNIT_VISUALS: Record<string, ShooterVisual | readonly ShooterVisual[]> = {
  // The bow, held at the hip.
  archer: { shot: 'arrow', muzzle: at(0.35) },
  // Rifles across the chests of the (smaller) squad figures.
  'rifle-squad': { shot: 'tracer', muzzle: at(0.36) },
  // The raised barrel's mouth.
  artillery: { shot: 'shell', muzzle: at(0.35) },
  // The tripod machine gun, low in front.
  'gun-squad': { shot: 'tracer', muzzle: at(0.17) },
  // The turret's barrel.
  tank: { shot: 'shell', muzzle: at(0.28) },
  // The tilted missile rack on the truck bed.
  'missile-launcher': { shot: 'missile', muzzle: at(0.36) },
  // The arm cannons.
  mech: { shot: 'beam', muzzle: at(0.43) },
  // The lead drone, hovering.
  'drone-swarm': { shot: 'tracer', muzzle: at(0.44) },
  // The missile's nose in the open shaft.
  'missile-silo': { shot: 'missile', muzzle: at(0.31, 0, -0.04) },
  // Arrows from the keep (L1–L4; it only fires shots from L3, range 2),
  // shells from the clock tower (L5) and the glass tower's roof (L6),
  // missiles from the spire's tip (L7).
  city: [
    { shot: 'arrow', muzzle: at(0.42) },
    { shot: 'arrow', muzzle: at(0.42) },
    { shot: 'arrow', muzzle: at(0.42) },
    { shot: 'arrow', muzzle: at(0.6) },
    { shot: 'shell', muzzle: at(0.55) },
    { shot: 'shell', muzzle: at(0.74, 0, -0.14) },
    { shot: 'missile', muzzle: at(1.02) },
  ],
};

/**
 * How a support unit's mending is drawn while it works: `heal` (soft green
 * motes drifting from the source to a unit it heals, joined by a faint green
 * beam) or `repair` (a short amber beam with sparks at the machine it
 * fixes), matching its rules `heals` (organic or mechanical). Client-only: the sim decides who heals whom; this says
 * where on the healer's model the effect starts and in which colour.
 */
export type HealKind = 'heal' | 'repair';

export interface HealVisual {
  kind: HealKind;
  /** Where the effect leaves the model, as for a `Muzzle`. */
  source: Muzzle;
  /** Colour of the beam and particles. */
  color: string;
}

/** The green of every heal (matches the healer's orb, `HEAL_GLOW`), and the amber of a repair. */
export const HEAL_COLOR = '#6ee88a';
export const REPAIR_COLOR = '#ffc24a';

/** Unit type → how its mending looks. Types not listed don't heal. */
export const HEAL_VISUALS: Record<string, HealVisual> = {
  // The glowing orb atop the staff.
  healer: { kind: 'heal', source: at(0.86), color: HEAL_COLOR },
  // The medkit in hand.
  medic: { kind: 'heal', source: at(0.3), color: HEAL_COLOR },
  // The wrench's jaws over the shoulder.
  engineer: { kind: 'repair', source: at(0.66), color: REPAIR_COLOR },
};

/** How a type's mending is drawn, or undefined for a type that doesn't heal. */
export function healVisualFor(type: string): HealVisual | undefined {
  return HEAL_VISUALS[type];
}

/** Where a shot leaves a model when a type isn't listed: about chest / roof height. */
export const DEFAULT_UNIT_MUZZLE: Muzzle = at(0.32);
const DEFAULT_BUILDING_MUZZLE: Muzzle = at(0.7);

/** How a type fires at `level` (1 for units), or undefined for melee. */
function shooterVisual(type: string, level = 1): ShooterVisual | undefined {
  const entry = UNIT_VISUALS[type];
  if (!entry || 'shot' in entry) return entry;
  return entry[Math.min(Math.max(level, 1), entry.length) - 1];
}

export function shotKindFor(type: string, level = 1): ShotKind | undefined {
  return shooterVisual(type, level)?.shot;
}

/** Where a type's shots leave its model at `level`, else the default for a unit or building. */
export function muzzleFor(type: string, building: boolean, level = 1): Muzzle {
  return (
    shooterVisual(type, level)?.muzzle ?? (building ? DEFAULT_BUILDING_MUZZLE : DEFAULT_UNIT_MUZZLE)
  );
}
